<script lang="ts">
	import {
		Container,
		Text,
		SelectCard,
		Row,
		Col,
		Token,
		Popover,
		Toggle,
		IconButton,
		CardGrid,
	} from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import { capFirstChar, _deepClone, wait } from '../../../../helpers/utilities';
	import type { ITempSchemeObj } from '../../../../types/TemperatureSchemeType';

	import { push } from 'svelte-spa-router';
	import { celsius_to_fahrenheit, fahrenheit_to_celsius } from '../../../../helpers/interface_helpers';
	import { devices } from 'src/stores/lists/devices';

	import { createEventDispatcher, onMount, getContext } from 'svelte';
	import DecreaseIncreaseInput from 'src/components/DecreaseIncreaseInput.svelte';
	import DecreaseIncreaseTempRange from 'src/components/DecreaseIncreaseTempRange.svelte';
	import { create_control } from '../../helpers/ui_to_temp_scheme';
	import { extractHexId } from '../../helpers/temp_scheme_to_ui';
	import { MAX_TEMPERATURE_LIMIT, MIN_TEMPERATURE_LIMIT } from 'src/constants';
	import { scrollToElementInWizard } from 'src/components/Lists/list-helpers';
	import { handleHysteresisError } from 'src/components/DevicesSettings/Settings/SettingsG2/ThermostatsWallDisplay/helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let temp_scheme_object: ITempSchemeObj;
	export let errorsSetupStep: boolean;
	const dispatch = createEventDispatcher();

	let mode = temp_scheme_object?._meta?.type;
	let units = temp_scheme_object.isinfh ? 'F' : 'C';

	let hysteresisError = '';
	let rangeError = '';

	let default_output = temp_scheme_object?._meta?.default_output || 'normal';
	let actuator_reset = temp_scheme_object.hasOwnProperty('actuator_reset')
		? temp_scheme_object.actuator_reset
		: true;
	let selected = temp_scheme_object.isinfh ? 'F' : 'C';
	let minValueHysteresis = 0.5;
	let maxValueHysteresis = 2;
	let cacheMode = mode,
		cacheUnit = units;

	let additionalContentElRef: HTMLElement = null;

	let thermostatWizardContainers: SvelteStore<{
		wizardContainerRef: HTMLElement;
		scrollContainerRef: HTMLElement;
	}> = getContext('thermostatWizardContainers');

	$: if (cacheMode !== mode || cacheUnit !== units) {
		handleRangeValidation();
		cacheMode = mode;
		cacheUnit = units;
	}

	let selectCards = [
		{
			id: 1,
			title: capFirstChar($format('label_heating')),
			selected: mode === 'heat',
			icon: 'fal fa-temperature-sun',
		},
		{
			id: 2,
			title: capFirstChar($format('label_cooling')),
			selected: mode === 'cool',
			icon: 'fal fa-temperature-snow',
		},
	];
	let selectedItem = mode === 'cool' ? 2 : mode === 'heat' ? 1 : undefined;

	// selectedActuatorAction set in what mode actuators are going to be when thermostat scene is manually disable
	$: selectedActuatorAction = temp_scheme_object.failsafe ? temp_scheme_object.failsafe : 'tocool';

	$: errorsSetupStep = rangeError?.length > 0 || hysteresisError?.length > 0;

	/** Because we have not more reactivity, we need to watch for changes (most cases on EDIT thermostat)*/
	$: if (temp_scheme_object?._meta?.type) {
		mode = temp_scheme_object?._meta?.type;
		selectedItem = mode === 'cool' ? 2 : mode === 'heat' ? 1 : undefined;
	}

	$: if (temp_scheme_object.isinfh) {
		units = temp_scheme_object.isinfh ? 'F' : 'C';
		selected = temp_scheme_object.isinfh ? 'F' : 'C';
	}

	$: if (temp_scheme_object?._meta?.default_output) {
		default_output = temp_scheme_object?._meta?.default_output || 'normal';
	}

	$: if (additionalContentElRef) {
		scrollToElementInWizard(
			$thermostatWizardContainers.wizardContainerRef,
			additionalContentElRef,
			$thermostatWizardContainers.scrollContainerRef,
		);
	}

	onMount(() => {
		if (!temp_scheme_object.hasOwnProperty('failsafe')) {
			temp_scheme_object['failsafe'] = 'tocool';
		}
		// temp_scheme_object.ths = temp_scheme_object.ths;
	});

	const onSelectActuatorAction = (e) => {
		selectedActuatorAction = e;
		temp_scheme_object.failsafe = selectedActuatorAction;
	};

	const onSelectCard = (array, i: number, multipleSelect = false) => {
		array = array.map((sc) => {
			if (sc.id === i) {
				return {
					...sc,
					selected: multipleSelect ? !sc.selected : true,
				};
			}
			return {
				...sc,
				selected: multipleSelect ? sc.selected : false,
			};
		});

		return array;
	};

	const change_default_output = (_default_output) => {
		default_output = _default_output;
		temp_scheme_object._meta.default_output = default_output;

		let selectedItems =
			temp_scheme_object?.tocool?.blk.length > 0
				? [
						...temp_scheme_object?.tocool?.blk.map((dev) => {
							let hexId = extractHexId(dev?.r || '');
							return $devices[hexId] || { id: hexId, name: 'deleted' };
						}),
					].filter((el) => el !== undefined)
				: [];

		let { toheat, tocool } = create_control({
			control: { type: 'relay' },
			data: {
				selectedItems,
				type: temp_scheme_object?._meta?.type,
				default_output: temp_scheme_object?._meta?.default_output,
			},
		});

		temp_scheme_object.toheat.blk = [...toheat.blk];
		temp_scheme_object.tocool.blk = [...tocool.blk];
	};

	const change_units = (e, selected) => {
		// let _copy: ITempSchemeObj = _deepClone(
		// 	$temp_scheme_object
		// ) as ITempSchemeObj;

		units = selected;

		console.log('selected ', selected);
		console.log('data config ', temp_scheme_object?._meta);

		temp_scheme_object.isinfh = selected === 'F';
		temp_scheme_object.ths = temp_scheme_object.ths;
		temp_scheme_object.deftemp =
			selected === 'F'
				? celsius_to_fahrenheit(temp_scheme_object.deftemp)
				: fahrenheit_to_celsius(temp_scheme_object.deftemp);

		if (temp_scheme_object?.intervals?.length > 0) {
			for (let ind = 0; ind < temp_scheme_object?.intervals?.length; ind++) {
				//update interval temp value to current units and update meta data for them
				temp_scheme_object.intervals[ind]['tschem_temp'] =
					selected === 'F'
						? celsius_to_fahrenheit(temp_scheme_object.intervals[ind]['tschem_temp'])
						: fahrenheit_to_celsius(temp_scheme_object.intervals[ind]['tschem_temp']);
				//remove meta data after shelly-app-2 deprecated

				if (temp_scheme_object?._meta?.conf && temp_scheme_object?._meta?.conf[ind]) {
					temp_scheme_object._meta.conf[ind]['temp_threshold'] =
						temp_scheme_object.intervals[ind]['tschem_temp'];
					temp_scheme_object._meta.conf[ind]['temp_units'] = selected === 'F' ? '°F' : '°C';
					console.log(JSON.stringify(temp_scheme_object._meta.conf[ind], null, 2));
				}
			}
		}

		//TODO : drop the whole unused and doubled data when shelly-app-2 is over
		temp_scheme_object._meta['threshold'] = temp_scheme_object.ths;
		temp_scheme_object._meta['temp_units'] = units;
		temp_scheme_object._meta['fh'] = temp_scheme_object.isinfh;
		temp_scheme_object._meta['base_temp'] = temp_scheme_object.deftemp;

		// temp_scheme_object.change(_copy);

		console.warn('new data after change units ', JSON.stringify(temp_scheme_object, null, 2));
	};

	const change_mode = (id) => {
		//change the mode only if it is different than the previous mode
		if (selectedItem !== id) {
			selectedItem = id;
			selectCards = onSelectCard(selectCards, id);
			//if the mode was change then we reset the default_output with 'normal'
			temp_scheme_object._meta.type = id === 1 ? 'heat' : 'cool';
			change_default_output('normal');
		}
	};

	const handleChangeTempUnit = async () => {
		// dispatch close because the modal is not closing if you click the link from here, it is closing only from the edit place
		dispatch('close');
		await wait(100);
		push('/settings/user?temperatureUnit');
	};

	function handleRangeValidation() {
		let maxTemp = temp_scheme_object._meta[units || 'C'][mode || 'cool'].max;
		let minTemp = temp_scheme_object._meta[units || 'C'][mode || 'cool'].min;
		rangeError =
			maxTemp.length === 0 ||
			maxTemp === undefined ||
			maxTemp === '' ||
			isNaN(maxTemp) ||
			+minTemp >= +maxTemp ||
			+maxTemp > MAX_TEMPERATURE_LIMIT[units] ||
			minTemp.length === 0 ||
			minTemp === undefined ||
			minTemp === '' ||
			isNaN(minTemp) ||
			+minTemp < MIN_TEMPERATURE_LIMIT[units]
				? $format('scene_invalid_format_err_toast')
				: '';
	}

	let showHysteresisPopover = false;
	let showActuatorsActionByDefaultPopover = false;
	let showActuatorsResetPopover = false;
</script>

<Container padding="scrollPaddings">
	<Text element="h2" class="mb-s">{$format('choose_thermostat_type')}</Text>
	<CardGrid class="mb-m" sm={200} md={200} xl={200} noPaddingBottom>
		{#each selectCards as s, i (s.id)}
			<SelectCard
				cardBgColor={$showNewLayout ? 'color-1' : undefined}
				class="width-full"
				type="horizontal"
				title={s.title}
				selected={s.selected}
				variant="radio"
				on:click={() => change_mode(s.id)}
				icon={s.icon}
			/>
		{/each}
	</CardGrid>
	<Text element="h2" class="mb-s">
		{$format('text_title_thermostat_temperature_unit', {
			default: 'Temperature unit',
		})}
	</Text>

	<div class="d-flex mb-m">
		<Token
			id="token-pw3h3i6"
			selected={selected === 'C'}
			size="m"
			title="°C"
			disabled={!selectedItem}
			class="mr-s"
			on:click={(e) => {
				selected = 'C';
				change_units(e, 'C');
			}}
		/>
		<Token
			id="token-btaaxri"
			selected={selected === 'F'}
			size="m"
			title="°F"
			disabled={!selectedItem}
			on:click={(e) => {
				selected = 'F';
				change_units(e, 'F');
			}}
		/>
	</div>

	<DecreaseIncreaseInput
		bind:error={hysteresisError}
		on:output={(e) => {
			temp_scheme_object.ths = e.detail;
			hysteresisError = handleHysteresisError(
				temp_scheme_object?.ths,
				minValueHysteresis,
				maxValueHysteresis,
			);
		}}
		bind:value={temp_scheme_object.ths}
		hysteresis
		allowedDigAfterPoint={2}
		inputClasses="mb-l"
	>
		<Text slot="description" element="h2" class="mb-s">
			{$format('label_hysteresis', { default: 'Hysteresis' })}
			<Popover
				hideCloseBtn={true}
				class="mr-m"
				show={showHysteresisPopover}
				placement="top"
				type="hover"
				on:close={() => (showHysteresisPopover = false)}
			>
				<IconButton
					aria-label={$format('button_info', { default: 'Button Info' })}
					icon="fa-solid fa-circle-info"
					variant="help"
					class="text-primary"
					on:click={() => {
						showHysteresisPopover = !showHysteresisPopover;
					}}
					size="s"
				/>
				<svelte:fragment slot="customContent">
					<Text styleType="body-small">
						{$format('description_hysteresis', {
							default:
								'Temperature hysteresis in a thermostat refers to the difference in temperature at which the thermostat turns on and turns off. When the temperature rises above the set point, the thermostat turns on the heating or cooling system. However, the temperature must drop below a certain point before the thermostat turns off the system. This temperature difference is known as hysteresis and helps prevent rapid cycling of the heating or cooling system, which can improve energy efficiency and reduce wear and tear on the equipment.',
						})}
					</Text>
				</svelte:fragment>
			</Popover>
		</Text>
	</DecreaseIncreaseInput>
</Container>

<Container padding="scrollPaddings">
	<hr class="mb-l" />
	<div class="d-flex">
		<Toggle bind:checked={temp_scheme_object._meta.advancedSettingsIsOpen} id="toggle-yx6ezmv">
			{$format('title_advanced_settings', {
				default: 'Advanced settings',
			})}
		</Toggle>
	</div>
	{#if temp_scheme_object._meta.advancedSettingsIsOpen}
		<div bind:this={additionalContentElRef}>
			<Text element="h2" class="mt-m">
				{$format('thermostat_default_output')}
			</Text>

			<div class="d-flex align-items-center flex-wrap">
				<Text class="mt-s mr-s" element="p" styleType="body-small" noWrap>
					{capFirstChar(
						$format('word_to', {
							default: 'To',
						}),
					)}
					<span class="text-primary text-bold">
						{$format(`mode_${mode || 'cool'}`).toUpperCase()}
					</span>
					{$format('word_turn_the_actuators', {
						default: 'turn the actuators',
					})}
				</Text>

				<div class="mt-s">
					<Token
						id="token-65cpii3"
						selected={default_output === 'normal'}
						size="m"
						title={capFirstChar($format('on'))}
						class="mr-xs"
						on:click={(e) => {
							change_default_output('normal');
						}}
					/>

					{#if mode === 'heat'}
						<Token
							id="token-2x6tn4p"
							selected={default_output === 'reversed'}
							size="m"
							title={capFirstChar($format('off'))}
							on:click={(e) => {
								change_default_output('reversed');
							}}
						/>
					{/if}
				</div>
			</div>

			<hr class="mb-m mt-m" />

			<div class="d-flex align-items-center">
				<Text class="mr-xs" element="h2">
					{$format('title_actuators_action_by_disabled_schedule', {
						default: 'Choose action when thermostat is disabled by а schedule.',
					})}
				</Text>
				<Popover
					class="align-self-start"
					show={showActuatorsActionByDefaultPopover}
					placement="top"
					hideCloseBtn
					type="hover"
					on:close={() => (showActuatorsActionByDefaultPopover = false)}
				>
					<IconButton
						aria-label={$format('button_info', { default: 'Button Info' })}
						icon="fa-solid fa-circle-info"
						variant="help"
						class="text-primary"
						on:click={() => {
							showActuatorsActionByDefaultPopover = !showActuatorsActionByDefaultPopover;
						}}
						size="s"
					/>
					<svelte:fragment slot="customContent">
						<Text styleType="body-small"
							>{$format('description_actuators_action_by_disabled_schedule', {
								default:
									'After thermostat is  disabled by a schedule, the chosen action will be triggered once and the thermostat will stay in that mode.',
							})}</Text
						>
					</svelte:fragment>
				</Popover>
			</div>

			<div class="d-flex align-items-center flex-wrap">
				<Text element="span" styleType="body-small" class="mt-s mr-s">
					{$format('option_actuators_default_action', {
						default: 'By default, the actuators are on',
					})}
				</Text>

				<div class="mt-s">
					<Token
						id="token-z2509c5"
						title={$format('mode_heat', { default: 'Heat' })}
						selected={selectedActuatorAction === 'toheat'}
						on:click={(e) => onSelectActuatorAction('toheat')}
						class="text-capitalize"
						size="m"
					/>
					<Token
						id="token-6pzgguj"
						title={$format('mode_cool', { default: 'Cool' })}
						selected={selectedActuatorAction === 'tocool'}
						on:click={(e) => onSelectActuatorAction('tocool')}
						class="text-capitalize"
						size="m"
					/>
				</div>
			</div>

			<hr class="mb-m mt-m" />

			<Text class="mb-s" element="h2">
				{$format('text_temp_limits', {
					default: 'Choose temperature limits',
				})}
				(°{units})
			</Text>

			<DecreaseIncreaseTempRange
				{mode}
				unit={units}
				on:error={(e) => {
					rangeError = e.detail.maxError || e.detail.minError;
				}}
				bind:maxTemp={temp_scheme_object._meta[units || 'C'][mode || 'cool'].max}
				bind:minTemp={temp_scheme_object._meta[units || 'C'][mode || 'cool'].min}
				bind:deftemp={temp_scheme_object.deftemp}
			>
				<Text slot="max input description" class="mb-s" element="p" styleType="body-small">
					{$format('text_max_temperature', {
						default: 'Max temperature',
					})}
				</Text>
				<Text slot="min input description" class="mb-s" element="p" styleType="body-small">
					{$format('text_min_temperature', {
						default: 'Min temperature',
					})}
				</Text>
			</DecreaseIncreaseTempRange>

			<hr class="mb-m mt-m" />

			<div class="d-flex align-items-center">
				<Text class="mr-xs" element="h2">
					{capFirstChar(
						$format('title_actuator_reset', {
							default: 'Reset actuators',
						}),
					)}
				</Text>
				<Popover
					class="mr-m"
					show={showActuatorsResetPopover}
					placement="top"
					hideCloseBtn={true}
					type="hover"
					on:close={() => (showActuatorsResetPopover = false)}
				>
					<IconButton
						aria-label={$format('button_info', { default: 'Button Info' })}
						icon="fa-solid fa-circle-info"
						variant="help"
						class="text-primary"
						on:click={() => {
							showActuatorsResetPopover = !showActuatorsResetPopover;
						}}
						size="s"
					/>
					<svelte:fragment slot="customContent">
						<Text styleType="body-small">
							{$format('description_actuator_reset', {
								default:
									'When set to "On", the thermostat will periodically set the correct state of the activating relay (based on the current thermostat temperature). So if anyone manually changes the state of the activating relay, it will be bring back to the desired state. This may take up to several minutes.',
							})}
						</Text>
					</svelte:fragment>
				</Popover>
			</div>
			<div class="d-flex align-items-center flex-wrap">
				<Text element="span" styleType="body-small" class="mt-s mr-s">
					{capFirstChar(
						$format('text_continuously_applicable', {
							default: 'Continuously applicable',
						}),
					)}
				</Text>

				<div class="mt-s">
					<Token
						id="token-8uxodmr"
						selected={actuator_reset}
						size="m"
						title={capFirstChar($format('on'))}
						class="mr-xs"
						on:click={(e) => {
							actuator_reset = true;
							temp_scheme_object.actuator_reset = true;
						}}
					/>
					<Token
						id="token-8dq1gyb"
						selected={!actuator_reset}
						size="m"
						title={capFirstChar($format('off'))}
						on:click={(e) => {
							actuator_reset = false;
							temp_scheme_object.actuator_reset = false;
						}}
					/>
				</div>
			</div>
		</div>
	{/if}
</Container>
